import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { BrandProvider } from './context/BrandContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { buildMsalConfig, clearStaleInteraction } from './auth/msalConfig';
import { ENTRA_LOGIN_ERROR_KEY, ENTRA_TOKEN_KEY, TEAMS_INTERACTIVE_AUTH_KEY } from './auth/constants';
import { TeamsAuthNotifySuccess } from './components/TeamsSsoPopupFlow';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import Layout from './components/Layout';
import Login from './pages/Login';
import { ADMIN_PAGE_ROLES } from './auth/constants';

/**
 * Pages load when they are opened, not when the app does.
 *
 * All fourteen were imported eagerly, so opening any page downloaded every page: a single
 * 1.05 MB main bundle, of which 673 KB was our own source and ~380 KB was the markdown
 * renderer that only the chat and drafting screens use. A lawyer opening /hesaplama paid for
 * the audit log, the admin panel and react-markdown before anything rendered.
 *
 * Login and Layout stay eager on purpose. Login is the first paint for a signed-out visitor
 * and Layout wraps every signed-in route, so splitting either one only adds a round trip
 * before the user sees anything.
 */
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const ContractAnalysis = React.lazy(() => import('./pages/ContractAnalysis'));
const LegislationDiff = React.lazy(() => import('./pages/LegislationDiff'));
const Audit = React.lazy(() => import('./pages/Audit'));
const CaseLaw = React.lazy(() => import('./pages/CaseLaw'));
const MinistryDecisions = React.lazy(() => import('./pages/MinistryDecisions'));
const Chat = React.lazy(() => import('./pages/Chat'));
const Settings = React.lazy(() => import('./pages/Settings'));
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));
const DraftingStudio = React.lazy(() => import('./pages/DraftingStudio'));
const Hesaplama = React.lazy(() => import('./pages/Hesaplama'));
const CaseFiles = React.lazy(() => import('./pages/CaseFiles'));
const FormatConverter = React.lazy(() => import('./pages/FormatConverter'));

/** Required on new Teams / M365 hosts (*.cloud.microsoft) so the tab shell loads the page. */
function TeamsHostInit() {
  useEffect(() => {
    let cancelled = false;
    import('@microsoft/teams-js')
      .then(({ app }) =>
        app.initialize().then(() => {
          if (cancelled) return;
          if (typeof localStorage !== 'undefined' && localStorage.getItem(TEAMS_INTERACTIVE_AUTH_KEY) === '1') {
            return;
          }
          if (typeof app.notifySuccess === 'function') app.notifySuccess();
        })
      )
      .catch(() => {
        /* Not inside Teams – expected for normal browser */
      });
    return () => { cancelled = true; };
  }, []);
  return null;
}

/** Waits for MSAL to initialize and process redirect from Microsoft login so accounts are populated. */
function MsalRedirectHandler({ children }) {
  const { instance } = useMsal();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    instance.initialize().then(() => instance.handleRedirectPromise()).then((result) => {
      // On return from Microsoft, persist the bearer (id) token and mark the
      // signed-in account active so the auth context and API client see it
      // immediately – without this the route guard bounces back to /login.
      if (result?.account) {
        try { instance.setActiveAccount(result.account); } catch {}
        if (result.idToken) {
          try { localStorage.setItem(ENTRA_TOKEN_KEY, result.idToken); } catch {}
        }
      }
      // Any interaction flag surviving handleRedirectPromise is leftover state; if it is
      // not cleared, the next sign-in fails with interaction_in_progress forever.
      clearStaleInteraction();
      if (!cancelled) setReady(true);
    }).catch((err) => {
      if (!cancelled) {
        console.error('MSAL init/redirect:', err);
        clearStaleInteraction();
        try {
          sessionStorage.setItem(
            ENTRA_LOGIN_ERROR_KEY,
            JSON.stringify({
              code: err.errorCode || err.code || '',
              message: err.errorMessage || err.message || String(err),
            })
          );
        } catch {}
        setReady(true);
      }
    });
    return () => { cancelled = true; };
  }, [instance]);

  if (!ready) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', flexDirection: 'column', gap: '1rem' }}>
        <p>Signing you in…</p>
      </div>
    );
  }
  return (
    <>
      <TeamsAuthNotifySuccess />
      {children}
    </>
  );
}

function PrivateRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  // Stats Admin and Firm Admin reach the page but see only the usage report - AdminPanel
  // enforces that, and the server scopes what the numbers contain.
  if (!ADMIN_PAGE_ROLES.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      {/* One boundary around every route rather than one per page: the fallback is only ever
          on screen for the time it takes to fetch one chunk, and a per-page boundary would
          mean fourteen identical copies of it. */}
      <React.Suspense fallback={<div className="route-loading" aria-busy="true" />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          {/* Chat is the first screen, not the dashboard — the Copilot shape opens on a
              conversation. The panel is still reachable at /panel from the sidebar. */}
          <Route index element={<Navigate to="/chat" replace />} />
          <Route path="panel" element={<Dashboard />} />
          <Route path="contract" element={<ContractAnalysis />} />
          <Route path="diff" element={<LegislationDiff />} />
          <Route path="caselaw" element={<CaseLaw />} />
          <Route path="ministry" element={<MinistryDecisions />} />
          <Route path="generator" element={<DraftingStudio />} />
          <Route path="donustur" element={<FormatConverter />} />
          <Route path="hesaplama" element={<Hesaplama />} />
          <Route path="dosyalar" element={<CaseFiles />} />
          <Route path="chat" element={<Chat />} />
          <Route path="audit" element={<Audit />} />
          <Route path="settings" element={<Settings />} />
          <Route path="admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </React.Suspense>
    </BrowserRouter>
  );
}

/**
 * Loads Entra IDs from the API so production works without REACT_APP_* at build time.
 */
function App() {
  const [authCfg, setAuthCfg] = useState(null);
  // The MSAL instance must be created exactly once. Building it in the render
  // body (the previous behavior) made a brand-new instance on every re-render,
  // which discarded the account captured by handleRedirectPromise and caused an
  // endless redirect back to /login after a successful Microsoft sign-in.
  const msalRef = useRef(null);
  const msalKeyRef = useRef('');

  useEffect(() => {
    const base = process.env.PUBLIC_URL || '';
    const fromEnv = () => ({
      tenantId: process.env.REACT_APP_ENTRA_TENANT_ID || null,
      clientId: process.env.REACT_APP_ENTRA_CLIENT_ID || null,
    });
    const mergeAuth = (data) => {
      const env = fromEnv();
      const tid = (data?.tenantId && String(data.tenantId).trim()) || env.tenantId;
      const cid = (data?.clientId && String(data.clientId).trim()) || env.clientId;
      const hasIds = !!(tid && cid);
      return {
        entraEnabled: hasIds,
        tenantId: tid,
        clientId: cid,
      };
    };
    fetch(`${base}/api/config/auth`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => setAuthCfg(mergeAuth(data)))
      .catch(() => setAuthCfg(mergeAuth(null)));
  }, []);

  if (!authCfg) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', flexDirection: 'column', gap: '0.75rem', color: 'var(--text-muted, #666)' }}>
        <div className="spinner" />
        <span>Loading…</span>
      </div>
    );
  }

  const entraOn = !!(authCfg.entraEnabled && authCfg.clientId && authCfg.tenantId);
  if (entraOn) {
    const key = `${authCfg.clientId}|${authCfg.tenantId}`;
    if (msalKeyRef.current !== key) {
      msalRef.current = new PublicClientApplication(
        buildMsalConfig({ clientId: authCfg.clientId, tenantId: authCfg.tenantId })
      );
      msalKeyRef.current = key;
    }
  }
  const msalInstance = entraOn ? msalRef.current : null;

  const inner = (
    <AuthProvider entraEnabled={entraOn}>
      <AppRoutes />
    </AuthProvider>
  );

  return (
    <ThemeProvider>
      <LanguageProvider>
        <BrandProvider>
          <TeamsHostInit />
          {msalInstance ? (
            <MsalProvider instance={msalInstance}>
              <MsalRedirectHandler>
                {inner}
              </MsalRedirectHandler>
            </MsalProvider>
          ) : (
            inner
          )}
        </BrandProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
